import moment from "moment";
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasPlural from "./hasPlural";

export const MONTH = "YYYY-MM";
export const ISO = "YYYY-MM-DD";

/**
 * Mesečni pregled radnika: šta je naručio po danima u izabranom mesecu (POST /user/orders-by-month, a za radnika
 * sa `monthUrl` taj naslov, npr. /user/food-orders-by-month za jela iz grupa).
 * Radnik je computed `employee` (podrazumevano store report/selectedEmployee); komponenta ga može
 * zameniti. Mesec kreće od kraja izabranog perioda.
 */
export default {
  mixins: [hasPrinting, hasPlural],
  data() {
    const to = this.$store.state.report.filters.dates.to;
    return {
      month: moment(to, ISO).isValid() ? moment(to, ISO).format(MONTH) : moment().format(MONTH),
      rows: [],
      loading: false,
      failed: false,
      showAllDays: false,
      requestId: 0,
    };
  },
  computed: {
    employee() {
      return this.$store.state.report.selectedEmployee;
    },
    /** Globalni "Štampa" štampa mesec samo kad je on jedini izveštaj na ekranu (telefon). */
    handlesGlobalPrint() {
      return true;
    },
    monthStart() {
      return moment(this.month, MONTH).startOf("month");
    },
    daysInMonth() {
      return this.monthStart.daysInMonth();
    },
    formatLocale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
    monthTitle() {
      const name = new Intl.DateTimeFormat(this.formatLocale, { month: "long" }).format(
        this.monthStart.toDate()
      );
      return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${this.monthStart.year()}`;
    },
    /** "Tekući mesec" / "Prošli mesec" / "Sledeći mesec" za susedne mesece. */
    monthHint() {
      const offset = this.monthStart.diff(moment().startOf("month"), "months");
      if (offset === 0) {
        return this.$t("report_mobile.month_current");
      }
      if (offset === -1) {
        return this.$t("report_mobile.presets.last_month");
      }
      return offset === 1 ? this.$t("report_mobile.month_next") : "";
    },
    /** Redovi API-ja grupisani po datumu: { "2026-10-19": [{ name, quantity, shift, restaurant }] } */
    byDate() {
      const map = {};
      this.rows.forEach((row) => {
        const date = String(row.date || "").slice(0, 10);
        if (!date) {
          return;
        }
        (map[date] = map[date] || []).push({
          key: `${row.meal_id ?? row.food_id ?? row.meal_name ?? row.food_name}-${row.shift}`,
          name: this.toLatin(row.meal_name || row.food_name || ""),
          quantity: row.quantity || 1,
          shift: this.shiftNumber(row.shift),
          restaurant: this.toLatin(row.restaurant_name || ""),
        });
      });
      return map;
    },
    /**
     * Red API-ja je jedno jelo, pa je narudžba jedan obrok: `order_id` kad ga red nosi, inače
     * datum + smena + restoran (supa, glavno jelo, salata i hleb iste smene su jedna narudžba).
     */
    totals() {
      const orders = new Set(
        this.rows.map((row) =>
          row.order_id
            ? `#${row.order_id}`
            : `${String(row.date || "").slice(0, 10)}|${row.shift ?? ""}|${row.restaurant_name ?? ""}`
        )
      );
      return {
        orders: orders.size,
        dishes: this.rows.reduce((sum, row) => sum + (row.quantity || 1), 0),
      };
    },
    multipleRestaurants() {
      return new Set(this.rows.map((row) => row.restaurant_name).filter(Boolean)).size > 1;
    },
    days() {
      const today = moment().format(ISO);
      const weekday = new Intl.DateTimeFormat(this.formatLocale, { weekday: "short" });
      const list = [];
      for (let day = 1; day <= this.daysInMonth; day += 1) {
        const date = this.monthStart.clone().date(day);
        const key = date.format(ISO);
        const items = this.byDate[key] || [];
        if (!items.length && !this.showAllDays) {
          continue;
        }
        const isWeekend = [0, 6].includes(date.day());
        list.push({
          key,
          num: `${day}.`,
          weekday: weekday.format(date.toDate()).replace(/\.$/, ""),
          items,
          detail: this.dayDetail(items),
          emptyText: isWeekend ? this.$t("report_mobile.weekend") : this.$t("report_mobile.no_order"),
          tileClass: key === today ? "is-today" : items.length ? "is-ordered" : "",
        });
      }
      return list;
    },
  },
  watch: {
    employee: {
      handler(employee) {
        if (employee) {
          this.loadMonth();
        }
      },
      immediate: true,
    },
    month() {
      this.loadMonth();
    },
    isPrinting(printing) {
      if (printing && this.handlesGlobalPrint) {
        this.print();
      }
    },
  },
  methods: {
    shiftNumber(shift) {
      if (Number.isInteger(shift)) {
        return shift;
      }
      const parsed = parseInt(String(shift || ""), 10);
      return Number.isNaN(parsed) ? null : parsed;
    },
    /** "Smena 1" / "Smene 1, 2" (+ restoran kad radnik jede kod više restorana). */
    dayDetail(items) {
      const shifts = [...new Set(items.map((item) => item.shift).filter(Boolean))].sort();
      const parts = [];
      if (shifts.length === 1) {
        parts.push(this.$t("report_mobile.shift_n", { n: shifts[0] }));
      } else if (shifts.length > 1) {
        parts.push(this.$t("report_mobile.shifts_list", { list: shifts.join(", ") }));
      }
      if (this.multipleRestaurants) {
        parts.push(...new Set(items.map((item) => item.restaurant).filter(Boolean)));
      }
      return parts.join(" · ");
    },
    stepMonth(step) {
      this.month = this.monthStart.clone().add(step, "month").format(MONTH);
    },
    selectMonth(month) {
      this.month = month;
    },
    async loadMonth() {
      if (!this.employee?.id) {
        return;
      }
      const requestId = ++this.requestId;
      this.loading = true;
      this.failed = false;
      try {
        const payload = {
          month: this.monthStart.month() + 1,
          year: this.monthStart.year(),
          date_from: this.monthStart.format(ISO),
          date_to: this.monthStart.clone().endOf("month").format(ISO),
          user_id: this.employee.id,
        };
        const { data } = this.employee.monthUrl
          ? await reportService.getEmployeeOrdersByMonthCustom(this.employee.monthUrl, payload)
          : await reportService.getEmployeeOrdersByMonth(payload);
        if (requestId === this.requestId) {
          this.rows = Array.isArray(data) ? data : [];
        }
      } catch (error) {
        if (requestId === this.requestId) {
          this.rows = [];
          this.failed = true;
        }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    print() {
      const list = this.showAllDays ? this.days : this.days.filter((day) => day.items.length);
      this.printDocument({
        title: this.employee?.fullName || "",
        subtitle: `${this.employee?.companyName || ""} · ${this.monthTitle}`,
        tables: [
          {
            headers: [
              { text: this.$t("date"), value: "date" },
              { text: this.$t("shift.shift"), value: "shift" },
              { text: this.$t("meals"), value: "meals" },
            ],
            items: list.map((day) => ({
              date: day.num,
              shift: day.items.length ? day.detail : "",
              meals: day.items.length
                ? day.items.map((item) => `${item.quantity}x ${item.name}`).join(", ")
                : day.emptyText,
            })),
          },
        ],
      });
    },
  },
};
